/**
 * Phase SC-21d (d11–d19) — IA seat-slot (shift) API client.
 *
 * Wraps the `manage_zones`-guarded surface in eduviaos-backend
 * `app/domains/seating/routes.py`:
 *
 *   GET    /api/seating/slots            (list, optional branch filter)
 *   POST   /api/seating/slots            (create — 409 `seat.slot_key_exists`)
 *   PATCH  /api/seating/slots/{slot_id}  (update — `key` is immutable)
 *
 * Wire shapes mirror `SeatSlotResponse`, `CreateSeatSlotRequest` and
 * `UpdateSeatSlotRequest` (both requests are `extra="forbid"`, so sending
 * an unknown field — including `key` on PATCH — is a 422).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

export type SeatSlotWeekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export const WEEKDAYS: readonly SeatSlotWeekday[] = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
];

export const WEEKDAY_SHORT: Record<SeatSlotWeekday, string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

/** Matches the BE `key` pattern. */
export const SLOT_KEY_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;

export interface SeatSlot {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  key: string;
  label: string;
  /** `HH:MM:SS`, centre-local. */
  start_time: string;
  end_time: string;
  spans_midnight: boolean;
  days_of_week: SeatSlotWeekday[];
  effective_from: string | null;
  effective_until: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SeatSlotListResponse {
  items: SeatSlot[];
  total: number;
}

export interface CreateSeatSlotInput {
  branch_id?: string | null;
  key: string;
  label: string;
  start_time: string;
  end_time: string;
  spans_midnight: boolean;
  days_of_week: SeatSlotWeekday[];
  effective_from?: string | null;
  effective_until?: string | null;
  display_order: number;
  is_active?: boolean;
}

export type UpdateSeatSlotInput = Partial<Omit<CreateSeatSlotInput, "key">>;

export const seatSlotKeys = {
  all: ["ia", "seating", "slots"] as const,
};

export function useSeatSlots() {
  return useQuery<SeatSlotListResponse>({
    queryKey: seatSlotKeys.all,
    queryFn: () => api.get<SeatSlotListResponse>("/api/seating/slots"),
    staleTime: 15_000,
  });
}

export function useCreateSeatSlot() {
  const qc = useQueryClient();
  return useMutation<SeatSlot, ApiError, CreateSeatSlotInput>({
    mutationFn: (body) => api.post<SeatSlot>("/api/seating/slots", body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: seatSlotKeys.all }),
  });
}

export function useUpdateSeatSlot() {
  const qc = useQueryClient();
  return useMutation<SeatSlot, ApiError, { id: string; body: UpdateSeatSlotInput }>({
    mutationFn: ({ id, body }) => api.patch<SeatSlot>(`/api/seating/slots/${id}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: seatSlotKeys.all }),
  });
}

/** Live list order: active first, then the learner-facing `display_order`. */
export function sortSlots(slots: SeatSlot[]): SeatSlot[] {
  return [...slots].sort(
    (a, b) =>
      Number(b.is_active) - Number(a.is_active) ||
      a.display_order - b.display_order ||
      a.key.localeCompare(b.key),
  );
}

/** `09:00:00` → `09:00` for `<input type="time">`. */
export function toTimeInput(value: string): string {
  return value.slice(0, 5);
}

/** `14:30:00` → `2:30 PM`. */
export function formatClock(value: string): string {
  const [hh = "0", mm = "00"] = value.split(":");
  const h = Number(hh);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${mm} ${suffix}`;
}

const LEGACY_RANGE_KEY = /^slot-(\d{2})-(\d{2})-(\d{2})-(\d{2})$/;

/**
 * Human name for a booking's `slot_key`: the centre's shift label plus its
 * hours when the key matches a configured shift, otherwise the hours encoded
 * in a pre-SC-21d `slot-HH-MM-HH-MM` key, otherwise the raw key.
 */
export function describeSlotKey(
  key: string,
  slots: readonly SeatSlot[],
): { label: string; hours: string | null } {
  const slot = slots.find((s) => s.key === key && s.is_active) ?? slots.find((s) => s.key === key);
  if (slot) {
    return {
      label: slot.label,
      hours: `${formatClock(slot.start_time)} – ${formatClock(slot.end_time)}`,
    };
  }
  const legacy = LEGACY_RANGE_KEY.exec(key);
  if (legacy) {
    const [, sh, sm, eh, em] = legacy;
    return { label: `${formatClock(`${sh}:${sm}`)} – ${formatClock(`${eh}:${em}`)}`, hours: null };
  }
  return { label: key, hours: null };
}

/** `Mon–Fri`, `Every day`, or `Mon, Wed, Sat`. */
export function formatDays(days: readonly SeatSlotWeekday[]): string {
  const set = new Set(days);
  if (set.size === 7) return "Every day";
  const ordered = WEEKDAYS.filter((d) => set.has(d));
  const weekdays = WEEKDAYS.slice(0, 5);
  if (ordered.length === 5 && weekdays.every((d) => set.has(d))) return "Mon–Fri";
  return ordered.map((d) => WEEKDAY_SHORT[d]).join(", ");
}
